import { Button } from "@/components/ui/button";
import { useTranslation } from "@/features/utils/i18n";
import { formatFileSize } from "@/features/utils/numbers";
import { FilesIcon, FileTextIcon, TrashIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { twMerge } from "tailwind-merge";

interface FileListProps {
  files: File[];
  disabled: boolean;
  className?: string;
  onRemove: (index: number) => void;
  onAddMore: () => void;
  maxFiles: number;
}

export const FileList = ({
  files,
  disabled,
  className,
  onRemove,
  onAddMore,
  maxFiles,
}: FileListProps) => {
  const { t } = useTranslation("fileUpload");
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const totalSize = files.reduce((acc, f) => acc + f.size, 0);

  return (
    <div
      className={twMerge(
        "relative border border-input rounded-sm p-4 w-full min-w-0",
        className,
      )}
    >
      <div className="flex items-start gap-4 w-full min-w-0">
        {/* Icon */}
        <div className="h-20 w-20 flex items-center justify-center bg-slate-200 rounded flex-shrink-0">
          <FilesIcon className="h-8 w-8 text-muted-foreground" weight="fill" />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">
            {t("fileCount", { count: files.length })}
          </p>
          <p className="text-xs text-muted-foreground mb-2">
            {t("total", { size: formatFileSize(totalSize) })}
          </p>

          {/* File list */}
          <div className="w-full min-w-0">
            {files.map((file, index) => (
              <div
                key={index}
                className="-my-1 flex items-center gap-2 text-xs h-10 group hover:bg-slate-500/10 px-2 py-1.5 rounded -mx-2 transition-colors w-full min-w-0"
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <FileTextIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                <span
                  className="flex-1 text-foreground min-w-0 overflow-hidden whitespace-nowrap truncate"
                  title={file.name}
                >
                  {file.name}
                </span>
                <span className="text-xs flex-shrink-0 text-muted-foreground whitespace-nowrap">
                  {formatFileSize(file.size)}
                </span>
                {hoveredIndex === index && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="xsicon"
                    onClick={() => onRemove(index)}
                    disabled={disabled}
                    className="flex-shrink-0 text-red-500 hover:text-red-500/90 transition-colors"
                  >
                    <TrashIcon weight="fill" />
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* Add more button */}
          {files.length < maxFiles && (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="mt-2"
              onClick={onAddMore}
              disabled={disabled}
            >
              {t("addMore")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
